import GrayCard from "components/gray-card";
import TextInput from "components/inputs/text-input";
import DashboardLayout from "components/layouts/dashboard-layout";
import PageLinks from "components/page-links";
import Typography from "components/typography";
import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Colors } from "utils/colors";
import ReleaseInformation from "./components/releaseInformation";
import SocialMediaButtons from "./components/socialmediaButtons";
import TrackLinks from "./components/trackLinks";
import EmbedAudio from "./components/embedAudio";
import { ISocialMediaIcon } from "constants/socialMediaIcons";
import PrimaryButton from "components/buttons/primary-button";
import { useNavigate, useSearchParams } from "react-router-dom";
import { RootState } from "redux/app/store";
import { useSelector } from "react-redux";
import { GetActiveArtist } from "services/user/getUser";
import api from "services/axiosInstance";

import { UploadService } from "services/upload";
import Toast from "components/toast";
import { IRatedFansData } from "types/ratedfansData";
import Spinner from "components/spinner";
import { imageUrlToFile } from "utils/imageUrlToFile";
import { audioUrlToFile } from "utils/audioUrlToFile";

export interface IReleaseInfo {
  coverImg: File | undefined;
  title: string;
  type: string;
  artist: string;
}
export interface ISMBtn {
  button: ISocialMediaIcon;
  url: string;
  index?: number;
}

export interface ITrackLink {
  name: string;
  url: string;
  logo?: React.ReactNode;
  index?: number;
}
export interface ITrackAudio {
  name: string;
  url: string;
  audio: File;
  index?: number;
}

interface ICreatePageLink {
  customSlug: string;
  releaseTitle: string;
  releaseType: string;
  artistName: string;
  socialMediaLinks?: {};
  previewClipUrl?: string;
  coverArtLink?: { url: string };
  previewClips?: { url: string; title: string }[];
}

interface IUpdatePageLink {
  customSlug?: string;
  releaseTitle?: string;
  releaseType?: string;
  artistName?: string;
  socialMediaLinks?: {};
  previewClipUrl?: string;
  coverArtLink?: { url: string } | undefined;
  previewClips?: { url: string; title: string }[] | undefined;
}

const CustomizeLink = () => {
  const [searchParams] = useSearchParams();
  const Slug = searchParams.get("slug");

  const [gettingSlugData, setGettingSlugData] = useState(false);
  const [slugData, setSlugData] = useState<IRatedFansData>();

  const activeArtist = GetActiveArtist();

  const [buttons, setButtons] = useState<ISMBtn[]>([]);
  const [links, setLinks] = useState<ITrackLink[]>([]);
  const [audios, setAudios] = useState<ITrackAudio[]>([]);

  //for edit mode
  const [changedCoverImg, setChangedCoverImg] = useState(false);
  const [changedAudio, setChangedAudio] = useState(false);
  const [uploading, setUploading] = useState(false);

  const navigate = useNavigate();

  const [pageUrl, setPageUrl] = useState(`https://ratedafrika.com/`);
  const [releaseInformation, setReleaseInformation] = useState<IReleaseInfo>({
    coverImg: undefined,
    title: "",
    type: "",
    artist: activeArtist?.name || ""
  });

  const EditMode = useMemo(() => {
    if (Slug) {
      return true;
    }
    return false;
  }, [Slug]);

  const OnChangePageUrl = (text: string) => {
    if (text.startsWith(`https://ratedafrika.com/`)) {
      setPageUrl(text);
    }
  };
  const handleChangeInfo = (text: string | File, key: string) => {
    if (key === "coverImg" && EditMode) {
      setChangedCoverImg(true);
    }
    setReleaseInformation({ ...releaseInformation, [key]: text });
  };

  const SaveDisabled = useMemo(() => {
    const releaseFilled =
      releaseInformation.coverImg &&
      releaseInformation.title &&
      releaseInformation.type &&
      releaseInformation.artist;

    const isUrlValid = pageUrl.startsWith(`https://ratedafrika.com/`);

    if (
      isUrlValid &&
      buttons.length > 0 &&
      links.length > 0 &&
      audios.length > 0 &&
      releaseFilled
    ) {
      return false;
    }
    return true;
  }, [pageUrl, releaseInformation, buttons, links, audios]);

  const CheckIfLinkExists = async () => {
    try {
      const response = await api.get(`/r/${pageUrl.split("/").pop()}`);

      if (response.data.data) {
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  };

  const CreateLink = async () => {
    if (uploading || SaveDisabled || !releaseInformation.coverImg) return;

    setUploading(true);

    const socialMediaLinksMap = buttons.reduce((acc, { button, url }) => {
      acc[button] = url;
      return acc;
    }, {} as { [key: string]: string });

    const trackLinksMap = links.map(({ name, url, index }, i) => ({
      platform: name,
      url: url
    }));

    const customSlug = pageUrl.split("/").pop();

    const body: ICreatePageLink = {
      customSlug: customSlug || "",
      releaseTitle: releaseInformation.title,
      releaseType: releaseInformation.type,
      artistName: releaseInformation.artist,
      socialMediaLinks: socialMediaLinksMap
    };
    try {
      const isLinkExists = await CheckIfLinkExists();

      if (isLinkExists) {
        Toast({
          error: "This url is already in use. Please try a different one."
        });
        return;
      }

      const uploadCoverImg = await UploadService(
        releaseInformation.coverImg,
        "image",
        true,
        true
      );

      body.coverArtLink = { url: uploadCoverImg };

      const previewClips = [];

      for (const audio of audios) {
        const uploadPreviewClip = await UploadService(
          audio.audio,
          "audio",
          true,
          true
        );
        previewClips.push({ url: uploadPreviewClip, title: audio.name });
      }
      body.previewClips = previewClips;

      const response = await api.post("/artist/ratedfans", body);

      const { data } = response;

      const id = data.data.id;

      await api.put(`/artist/ratedfans/${id}/links`, {
        links: trackLinksMap
      });

      await api.post(`/artist/ratedfans/${id}/publish`, {
        isPublished: true
      });

      Toast({ type: "success", message: "Link created successfully" });
      navigate(`/${customSlug}`);
    } catch (error) {
      Toast({ error: error as string });
    } finally {
      setUploading(false);
    }
  };

  const UpdateLink = async () => {
    if (uploading || SaveDisabled || !releaseInformation.coverImg || !slugData)
      return;

    const customSlug = pageUrl.split("/").pop();

    const changedDataBody: IUpdatePageLink = {};

    if (customSlug !== Slug) {
      changedDataBody.customSlug = customSlug || "";
    }
    if (releaseInformation.title !== slugData.releaseTitle) {
      changedDataBody.releaseTitle = releaseInformation.title;
    }
    if (releaseInformation.type !== slugData.releaseType) {
      changedDataBody.releaseType = releaseInformation.type;
    }
    if (releaseInformation.artist !== slugData.artistName) {
      changedDataBody.artistName = releaseInformation.artist;
    }

    const socialMediaLinksMap = buttons.reduce((acc, { button, url }) => {
      acc[button] = url;
      return acc;
    }, {} as { [key: string]: string });

    changedDataBody.socialMediaLinks = socialMediaLinksMap;

    const trackLinksMap = links.map(({ name, url, index }, i) => ({
      platform: name,
      url: url
    }));

    try {
      setUploading(true);
      const isLinkExists = await CheckIfLinkExists();

      if (!isLinkExists) {
        Toast({
          error: "This url is not in use. Please try a different one."
        });
        return;
      }

      if (changedCoverImg) {
        const uploadCoverImg = await UploadService(
          releaseInformation.coverImg,
          "image",
          true,
          true
        );

        changedDataBody.coverArtLink = { url: uploadCoverImg };
      }

      if (changedAudio) {
        const previewClips = [];

        for (const audio of audios) {
          const uploadPreviewClip = await UploadService(
            audio.audio,
            "audio",
            true,
            true
          );
          previewClips.push({ url: uploadPreviewClip, title: audio.name });
        }
        changedDataBody.previewClips = previewClips;
      }

      const response = await api.patch(
        `/artist/ratedfans/${slugData.id}`,
        changedDataBody
      );

      const { data } = response;

      const id = data.data.id;

      await api.put(`/artist/ratedfans/${id}/links`, {
        links: trackLinksMap
      });

      Toast({ type: "success", message: "Link updated successfully" });
      navigate(`/${customSlug}`);
    } catch (error) {
      Toast({ error: error as string });
    } finally {
      setUploading(false);
    }
  };

  const OnSave = () => {
    if (EditMode) {
      UpdateLink();
    } else {
      CreateLink();
    }
  };

  useEffect(() => {
    if (EditMode) {
      const GetSlugData = async () => {
        setGettingSlugData(true);
        try {
          const response = await api.get(`/r/${Slug}`);
          const { data } = response;
          const slugData: IRatedFansData = data.data;
          setSlugData(slugData);

          if (!slugData) {
            throw new Error("Something went wrong");
          }

          setPageUrl(`https://ratedafrika.com/${Slug}`);

          const coverImageFile = await imageUrlToFile(
            slugData.coverArtLink.url,
            `cover-${slugData.releaseTitle}.jpg`
          );

          setReleaseInformation({
            coverImg: coverImageFile,
            title: slugData.releaseTitle,
            type: slugData.releaseType,
            artist: slugData.artistName
          });

          setButtons(
            Object.entries(slugData.socialMediaLinks).map(
              ([platform, url]) => ({
                button: platform as ISocialMediaIcon,
                url: url
              })
            )
          );

          setLinks(
            slugData.links.map(({ platform, url }) => ({
              name: platform,
              url: url
            }))
          );

          // const audioPromises = slugData.previewClips.map(
          //   async ({ url, title }) => {
          //     const audioFile = await audioUrlToFile(url, `audio-${title}.mp3`);
          //     return {
          //       name: title,
          //       url: url,
          //       audio: audioFile
          //     };
          //   }
          // );

          // const resolvedAudios = await Promise.all(audioPromises);
          setAudios(
            slugData.previewClips.map(({ url, title }) => ({
              name: title,
              url: url,
              audio: new File([], url)
            }))
          );
        } catch (error) {
          Toast({ error: error as string });
        } finally {
          setGettingSlugData(false);
        }
      };

      GetSlugData();
    }
  }, [EditMode, Slug]);

  if (gettingSlugData) {
    return (
      <DashboardLayout>
        <div
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "80vh"
          }}
        >
          <Spinner width="5rem" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <>
        <PageLinks
          from={{ name: "RAted Fans", link: "/overview/rated-fans" }}
          current="Customize Link"
        />
        <Header>
          <Typography size="2xl" weight="semibold" color={Colors.white100}>
            Customize Link
          </Typography>
        </Header>
        <GrayCard title="My Page URL">
          <TextInput
            placeholder={`ratedafrika.com/`}
            value={pageUrl}
            onChange={(e) => OnChangePageUrl(e.target.value)}
          />
        </GrayCard>
        <ReleaseInformation
          data={releaseInformation}
          onChangeData={(text, key) => handleChangeInfo(text, key)}
        />
        <SocialMediaButtons
          buttons={buttons}
          setButtons={(btns) => setButtons(btns)}
        />
        <TrackLinks links={links} setLinks={(links) => setLinks(links)} />
        <EmbedAudio
          audios={audios}
          setAudios={(audios) => {
            setAudios(audios);

            if (EditMode) {
              setChangedAudio(true);
            }
          }}
        />
        <SaveContainer>
          <PrimaryButton
            text={EditMode ? "Save Edit" : "Save"}
            fitContent
            disabled={SaveDisabled}
            onClick={OnSave}
            loading={uploading}
          />
        </SaveContainer>
      </>
    </DashboardLayout>
  );
};

export default CustomizeLink;

const Header = styled.div`
  width: 100%;
  padding: 1.6rem 0;
  border-bottom: 1px solid ${Colors.grey700};
  margin-bottom: 4rem;
`;

const SaveContainer = styled.div`
  margin-top: 5.1rem;
  display: flex;
  align-items: center;
  justify-content: end;
  width: 100%;
  padding: 2rem 0;
  border-top: 1px solid ${Colors.grey700};
`;
